import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { X, Search, Sigma, RotateCcw, Sparkles, Check, Minus, HelpCircle, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { MindMapNode } from '../types';
import { MathSymbolPickerModal } from './MathSymbolPickerModal';
import { judgeReplacementMatches, MatchJudgement, generateFindReplaceFields } from '../services/geminiService';

interface Match {
    nodeId: string;
    nodeTitle: string;
    field: 'title' | 'content';
    index: number;
    matchLength: number;
    context: string;
    contextMatchStart: number;
    contextMatchEnd: number;
}

interface UndoEntry {
    nodeId: string;
    field: 'title' | 'content';
    original: string;
}

interface Props {
    nodes: MindMapNode[];
    language?: string;
    onClose: () => void;
    onJumpToNode: (nodeId: string) => void;
    onUpdateNode: (id: string, data: Partial<MindMapNode>) => void;
    expandedNodeId?: string | null;
    onOpenExpanded?: (nodeId: string) => void;
    onQueryChange?: (query: string) => void;
    scope?: 'notebook' | 'note';
    initialQuery?: string;
}

function escapeRegex(s: string) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Returns positions of ** that are NOT part of a valid **bold** pair.
// Uses a simple greedy state machine: scan left-to-right, track open/close.
function findOrphanedDoubleStars(text: string): number[] {
    const positions: number[] = [];
    let openPos: number | null = null;
    let i = 0;
    while (i < text.length - 1) {
        if (text[i] === '*' && text[i + 1] === '*' && text[i - 1] !== '*' && text[i + 2] !== '*') {
            if (openPos === null) {
                openPos = i; // tentative open
            } else {
                openPos = null; // matched pair — both are valid bold, discard
            }
            i += 2;
        } else {
            i++;
        }
    }
    // Any unclosed open is orphaned
    if (openPos !== null) positions.push(openPos);
    return positions;
}

// Same scan but collects ALL orphaned positions (both unmatched opens and closes).
function findAllOrphanedDoubleStars(text: string): number[] {
    const allStars: number[] = [];
    let i = 0;
    while (i < text.length - 1) {
        if (text[i] === '*' && text[i + 1] === '*' && (i === 0 || text[i - 1] !== '*') && text[i + 2] !== '*') {
            allStars.push(i);
            i += 2;
        } else {
            i++;
        }
    }
    // Pair them up greedily: odd-indexed ones are orphaned opens, even-count remainder is orphaned
    const orphaned: number[] = [];
    // Use a stack: push opens, pop on close. Unpaired = orphaned.
    const stack: number[] = [];
    for (const pos of allStars) {
        if (stack.length === 0) {
            stack.push(pos);
        } else {
            stack.pop(); // matched pair — both valid
        }
    }
    // Remaining in stack are orphaned opens
    orphaned.push(...stack);
    return orphaned;
}

function buildMatches(nodes: MindMapNode[], query: string, caseSensitive: boolean, regexMode: boolean): Match[] {
    if (!query.trim()) return [];

    // Smart orphan mode: when query is exactly ** (literal or regex), find only unmatched **
    const isOrphanMode = query === '**' || (regexMode && query === '\\*\\*');

    const matches: Match[] = [];

    if (isOrphanMode) {
        for (const node of nodes) {
            const searchIn = (text: string, field: 'title' | 'content') => {
                const orphans = findAllOrphanedDoubleStars(text);
                for (const idx of orphans) {
                    const ctxStart = Math.max(0, idx - 40);
                    const ctxEnd = Math.min(text.length, idx + 2 + 40);
                    matches.push({
                        nodeId: node.id,
                        nodeTitle: node.title,
                        field,
                        index: idx,
                        matchLength: 2,
                        context: text.slice(ctxStart, ctxEnd),
                        contextMatchStart: idx - ctxStart,
                        contextMatchEnd: idx - ctxStart + 2,
                    });
                }
            };
            if (node.title) searchIn(node.title, 'title');
            if (node.content) searchIn(node.content, 'content');
        }
        return matches;
    }

    const flags = (caseSensitive || regexMode) ? 'g' : 'gi';
    let regex: RegExp;
    try {
        regex = new RegExp(regexMode ? query : escapeRegex(query), flags);
    } catch { return []; }

    for (const node of nodes) {
        const searchIn = (text: string, field: 'title' | 'content') => {
            for (const m of text.matchAll(regex)) {
                const idx = m.index!;
                const matchLen = m[0].length;
                const ctxStart = Math.max(0, idx - 40);
                const ctxEnd = Math.min(text.length, idx + matchLen + 40);
                matches.push({
                    nodeId: node.id,
                    nodeTitle: node.title,
                    field,
                    index: idx,
                    matchLength: matchLen,
                    context: text.slice(ctxStart, ctxEnd),
                    contextMatchStart: idx - ctxStart,
                    contextMatchEnd: idx - ctxStart + matchLen,
                });
            }
        };
        if (node.title) searchIn(node.title, 'title');
        if (node.content) searchIn(node.content, 'content');
    }
    return matches;
}

function applyReplaceAll(text: string, query: string, replacement: string, caseSensitive: boolean, regexMode: boolean): string {
    try {
        const pattern = regexMode ? query : escapeRegex(query);
        const flags = regexMode ? 'g' : (caseSensitive ? 'g' : 'gi');
        return text.replace(new RegExp(pattern, flags), replacement);
    } catch { return text; }
}

function resolveCaptures(replacement: string, text: string, query: string, matchIndex: number, regexMode: boolean): string {
    if (!regexMode || !replacement.includes('$')) return replacement;
    try {
        const m = new RegExp(query).exec(text.slice(matchIndex));
        if (!m) return replacement;
        return replacement.replace(/\$(\d+)/g, (_, n) => m[parseInt(n)] ?? '');
    } catch { return replacement; }
}

interface ChatMessage { role: 'user' | 'ai'; text: string; }

export const FindReplacePanel: React.FC<Props> = ({ nodes, language, onClose, onJumpToNode, onUpdateNode, expandedNodeId, onOpenExpanded, onQueryChange, scope = 'notebook', initialQuery }) => {
    const vn = language === 'vietnamese';
    const [query, setQuery] = useState(initialQuery || '');
    const [replacement, setReplacement] = useState('');
    const caseSensitive = false;
    const [regexMode, setRegexMode] = useState(false);
    const [replacedCount, setReplacedCount] = useState<number | null>(null);
    const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
    const [symbolPickerOpen, setSymbolPickerOpen] = useState(false);
    const [confirmingReplaceAll, setConfirmingReplaceAll] = useState(false);
    const searchRef = useRef<HTMLInputElement>(null);
    const replaceRef = useRef<HTMLInputElement>(null);

    // AI judge state
    type AIDecision = 'replace' | 'skip' | 'uncertain';
    interface AIResult { decision: AIDecision; confidence: number; reason: string; }
    const [aiResults, setAiResults] = useState<Record<string, AIResult>>({});
    const [aiRunning, setAiRunning] = useState(false);
    const [aiProgress, setAiProgress] = useState<{ done: number; total: number } | null>(null);
    const [aiThreshold, setAiThreshold] = useState(80);
    const aiAbortRef = useRef(false);

    // Dedicated hint for AI judge (user types context for what a "correct" match looks like)
    const [judgeHint, setJudgeHint] = useState('');

    // Pending AI judge trigger — set when chatbot asks to evaluate matches
    const [pendingJudge, setPendingJudge] = useState(false);

    // Chat state (session-only — cleared when panel unmounts)
    const [chatOpen, setChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);

    // Derived AI hint from recent chat (last 3 user messages)
    const derivedAiHint = useMemo(() =>
        chatMessages.filter(m => m.role === 'user').slice(-3).map(m => m.text).join('; '),
    [chatMessages]);

    useEffect(() => {
        if (chatOpen) chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages, chatLoading, chatOpen]);

    const handleChatSend = async () => {
        if (!chatInput.trim() || chatLoading) return;
        const userMsg = chatInput.trim();
        setChatInput('');
        const updated: ChatMessage[] = [...chatMessages, { role: 'user', text: userMsg }];
        setChatMessages(updated);
        setChatLoading(true);
        try {
            // Pass conversation history as context
            const context = updated.length > 1
                ? updated.slice(0, -1).map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.text}`).join('\n') + '\nUser: ' + userMsg
                : userMsg;
            const fields = await generateFindReplaceFields(context);
            setQuery(fields.query);
            setRegexMode(fields.regexMode);
            setReplacement(fields.replacement);
            if (fields.triggerJudge) {
                setChatOpen(true);
                setPendingJudge(true);
            }
            const parts: string[] = [];
            if (fields.query) parts.push(`Tìm: \`${fields.query}\``);
            if (fields.replacement) parts.push(`Thay: \`${fields.replacement}\``);
            if (fields.regexMode) parts.push('Regex bật');
            if (fields.hint) parts.push(fields.hint);
            if (fields.triggerJudge) parts.push(vn ? '→ Đang chạy AI đánh giá từng kết quả…' : '→ Running AI judge on each match…');
            const reply = parts.length > 0
                ? `✓ ${parts.join(' · ')}`
                : (vn ? 'Đã hiểu, nhưng không tạo được pattern.' : 'Understood, but could not generate a pattern.');
            setChatMessages(prev => [...prev, { role: 'ai', text: reply }]);
        } catch {
            setChatMessages(prev => [...prev, { role: 'ai', text: vn ? 'Có lỗi xảy ra, thử lại.' : 'Error, please try again.' }]);
        }
        setChatLoading(false);
    };

    useEffect(() => { searchRef.current?.focus(); }, []);
    useEffect(() => {
        setReplacedCount(null);
        setUndoStack([]);
        setConfirmingReplaceAll(false);
        setAiResults({});
        setAiProgress(null);
        aiAbortRef.current = true;
        if (query && /[\\^$()*+?[\]{}|]/.test(query)) setRegexMode(true);
    }, [query]);
    useEffect(() => { onQueryChange?.(query); }, [query, onQueryChange]);

    const handleNoteClick = (nodeId: string) => {
        if (expandedNodeId !== undefined && onOpenExpanded) {
            onOpenExpanded(nodeId);
        } else {
            onJumpToNode(nodeId);
        }
    };

    const isOrphanMode = query === '**' || (regexMode && query === '\\*\\*');
    const matches = useMemo(() => buildMatches(nodes, query, caseSensitive, regexMode), [nodes, query, caseSensitive, regexMode]);

    const grouped = useMemo(() => {
        const map = new Map<string, Match[]>();
        for (const m of matches) {
            if (!map.has(m.nodeId)) map.set(m.nodeId, []);
            map.get(m.nodeId)!.push(m);
        }
        return map;
    }, [matches]);

    const matchKey = (m: Match) => `${m.nodeId}::${m.field}::${m.index}`;

    const handleReplaceOne = (match: Match) => {
        const node = nodes.find(n => n.id === match.nodeId);
        if (!node) return;
        const original = match.field === 'title' ? node.title : node.content;
        let newText: string;
        if (regexMode) {
            try {
                const r = new RegExp(query, '');
                newText = original.slice(0, match.index) + original.slice(match.index).replace(r, replacement);
            } catch { return; }
        } else {
            newText = original.slice(0, match.index) + replacement + original.slice(match.index + match.matchLength);
        }
        setUndoStack(prev => [{ nodeId: match.nodeId, field: match.field, original }, ...prev.slice(0, 9)]);
        setReplacedCount(null);
        onUpdateNode(match.nodeId, match.field === 'title' ? { title: newText } : { content: newText });
    };

    const handleUndo = (entry: UndoEntry) => {
        onUpdateNode(entry.nodeId, entry.field === 'title' ? { title: entry.original } : { content: entry.original });
        setUndoStack(prev => prev.filter(e => e !== entry));
    };

    const handleReplaceAll = () => {
        if (!query || matches.length === 0) return;
        setConfirmingReplaceAll(true);
    };

    const handleConfirmReplaceAll = () => {
        setConfirmingReplaceAll(false);
        if (!query || matches.length === 0) return;
        let count = 0;
        const newUndos: UndoEntry[] = [];
        for (const [nodeId, nodeMatches] of grouped) {
            const node = nodes.find(n => n.id === nodeId);
            if (!node) continue;
            const update: Partial<MindMapNode> = {};
            const titleHits = nodeMatches.filter(m => m.field === 'title');
            const contentHits = nodeMatches.filter(m => m.field === 'content');
            if (titleHits.length) {
                newUndos.push({ nodeId, field: 'title', original: node.title });
                update.title = applyReplaceAll(node.title, query, replacement, caseSensitive, regexMode);
                count += titleHits.length;
            }
            if (contentHits.length) {
                newUndos.push({ nodeId, field: 'content', original: node.content });
                update.content = applyReplaceAll(node.content, query, replacement, caseSensitive, regexMode);
                count += contentHits.length;
            }
            if (Object.keys(update).length) onUpdateNode(nodeId, update);
        }
        setUndoStack(prev => [...newUndos, ...prev].slice(0, 20));
        setReplacedCount(count);
    };

    const handleAIAssist = useCallback(async () => {
        if (!query.trim() || matches.length === 0 || aiRunning) return;
        if (!chatOpen) setChatOpen(true);
        aiAbortRef.current = false;
        setAiRunning(true);
        const unchecked = [...grouped.entries()].filter(([, nodeMatches]) =>
            nodeMatches.some(m => !aiResults[matchKey(m)])
        );
        setAiProgress({ done: 0, total: unchecked.length });
        let done = 0;
        for (const [nodeId, nodeMatches] of unchecked) {
            if (aiAbortRef.current) break;
            const node = nodes.find(n => n.id === nodeId);
            const uncheckedMatches = nodeMatches.filter(m => !aiResults[matchKey(m)]);
            try {
                const payload = uncheckedMatches.map(m => ({ context: m.context }));
                const effectiveHint = judgeHint.trim() || derivedAiHint;
                const judgements = await judgeReplacementMatches(query, replacement, node?.title || '', payload, effectiveHint);
                const batch: Record<string, AIResult> = {};
                judgements.forEach((r, i) => {
                    if (uncheckedMatches[i]) batch[matchKey(uncheckedMatches[i])] = { decision: r.decision, confidence: r.confidence, reason: r.reason };
                });
                setAiResults(prev => ({ ...prev, ...batch }));
            } catch { }
            done++;
            setAiProgress({ done, total: unchecked.length });
        }
        setAiRunning(false);
        setAiProgress(null);
    }, [query, replacement, matches, grouped, nodes, aiRunning, aiResults, derivedAiHint, chatOpen]);

    const aiReplaceCount = useMemo(() =>
        matches.filter(m => {
            const r = aiResults[matchKey(m)];
            return r && r.decision === 'replace' && r.confidence >= aiThreshold;
        }).length,
    [matches, aiResults, aiThreshold]);

    const hasAnyAIResult = useMemo(() => Object.keys(aiResults).length > 0, [aiResults]);

    // Auto-trigger AI judge after query+matches update (triggered by chatbot's triggerJudge flag)
    useEffect(() => {
        if (pendingJudge && query.trim() && matches.length > 0 && !aiRunning) {
            setPendingJudge(false);
            handleAIAssist();
        }
    }, [pendingJudge, query, matches, aiRunning, handleAIAssist]);

    const handleApplyAIChoices = () => {
        const toReplace = matches.filter(m => {
            const r = aiResults[matchKey(m)];
            return r && r.decision === 'replace' && r.confidence >= aiThreshold;
        });
        if (toReplace.length === 0) return;
        const byNode = new Map<string, Match[]>();
        for (const m of toReplace) {
            if (!byNode.has(m.nodeId)) byNode.set(m.nodeId, []);
            byNode.get(m.nodeId)!.push(m);
        }
        const newUndos: UndoEntry[] = [];
        let count = 0;
        for (const [nodeId, nodeMatches] of byNode) {
            const node = nodes.find(n => n.id === nodeId);
            if (!node) continue;
            const update: Partial<MindMapNode> = {};
            const titleHits = nodeMatches.filter(m => m.field === 'title').sort((a, b) => b.index - a.index);
            const contentHits = nodeMatches.filter(m => m.field === 'content').sort((a, b) => b.index - a.index);
            if (titleHits.length) {
                newUndos.push({ nodeId, field: 'title', original: node.title });
                let t = node.title;
                for (const m of titleHits) {
                    const repl = resolveCaptures(replacement, t, query, m.index, regexMode);
                    t = t.slice(0, m.index) + repl + t.slice(m.index + m.matchLength);
                }
                update.title = t;
                count += titleHits.length;
            }
            if (contentHits.length) {
                newUndos.push({ nodeId, field: 'content', original: node.content });
                let c = node.content;
                for (const m of contentHits) {
                    const repl = resolveCaptures(replacement, c, query, m.index, regexMode);
                    c = c.slice(0, m.index) + repl + c.slice(m.index + m.matchLength);
                }
                update.content = c;
                count += contentHits.length;
            }
            if (Object.keys(update).length) onUpdateNode(nodeId, update);
        }
        setUndoStack(prev => [...newUndos, ...prev].slice(0, 20));
        setReplacedCount(count);
        setAiResults({});
        setChatMessages(prev => [...prev, { role: 'ai', text: `✓ ${vn ? `Đã thay ${count} kết quả` : `Applied ${count} replacements`}` }]);
    };

    return (
        <>
            <div className="h-full w-full flex flex-col bg-white dark:bg-slate-800 overflow-hidden">

                {/* Header */}
                <div className="flex items-center gap-2 px-5 py-3.5 border-b dark:border-slate-700 bg-gray-50 dark:bg-slate-900 shrink-0">
                    <Search size={14} className="text-gray-400 shrink-0" />
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex-1">
                        {scope === 'note'
                            ? (vn ? 'Tìm & Thay thế trong Note này' : 'Find & Replace in this Note')
                            : (vn ? 'Tìm & Thay thế trong Notebook' : 'Find & Replace in Notebook')}
                    </span>
                    <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded font-mono">
                        {scope === 'note'
                            ? (navigator.platform.includes('Mac') ? '⌘F' : 'Ctrl+F')
                            : (navigator.platform.includes('Mac') ? '⌘⇧F' : 'Ctrl+Shift+F')}
                    </span>
                    <button onClick={onClose} className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors text-gray-400 ml-1">
                        <X size={14} />
                    </button>
                </div>

                {/* Controls */}
                <div className="px-4 py-3 space-y-2.5 border-b dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0">

                    {/* Find input — inline × clear + .* regex toggle */}
                    <div className="relative">
                        <input
                            ref={searchRef}
                            type="text"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
                            placeholder={regexMode ? 'Regex pattern…' : (vn ? 'Từ cần tìm…' : 'Find…')}
                            className={`w-full pl-3 pr-16 py-2 text-sm bg-gray-50 dark:bg-slate-700 border rounded-xl outline-none text-gray-800 dark:text-gray-200 placeholder-gray-400 ${regexMode ? 'border-violet-300 dark:border-violet-600 focus:border-violet-500 font-mono' : 'border-gray-200 dark:border-slate-600 focus:border-blue-400 dark:focus:border-blue-500'}`}
                        />
                        {query && (
                            <button onClick={() => setQuery('')}
                                className="absolute right-10 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                                <X size={12} />
                            </button>
                        )}
                        <button
                            onClick={() => setRegexMode(v => !v)}
                            title={vn ? 'Bật/tắt Regex mode' : 'Toggle Regex mode'}
                            className={`absolute right-2 top-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded-md text-[11px] font-mono font-bold transition-colors ${regexMode ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300' : 'text-gray-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20'}`}
                        >
                            .*
                        </button>
                    </div>

                    {/* Replace input — inline × clear + Σ symbol picker */}
                    <div className="relative">
                        <input
                            ref={replaceRef}
                            type="text"
                            value={replacement}
                            onChange={e => setReplacement(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Escape') { if (confirmingReplaceAll) setConfirmingReplaceAll(false); else onClose(); }
                                if (e.key === 'Enter') handleReplaceAll();
                            }}
                            placeholder={vn ? 'Thay thế bằng…' : 'Replace with…'}
                            className="w-full pl-3 pr-16 py-2 text-sm bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl outline-none focus:border-green-400 dark:focus:border-green-500 text-gray-800 dark:text-gray-200 placeholder-gray-400"
                        />
                        {replacement && (
                            <button onClick={() => setReplacement('')}
                                className="absolute right-10 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                                <X size={12} />
                            </button>
                        )}
                        <button
                            onClick={() => setSymbolPickerOpen(true)}
                            title={vn ? 'Chèn ký hiệu toán học' : 'Insert math symbol'}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                        >
                            <Sigma size={14} />
                        </button>
                    </div>

                    {/* Replace All — full-width row */}
                    <button
                        onClick={handleReplaceAll}
                        disabled={!query.trim() || matches.length === 0}
                        className="w-full py-2 text-xs font-semibold rounded-xl bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-white disabled:opacity-40 transition-colors"
                    >
                        {vn ? 'Thay tất cả' : 'Replace All'}
                    </button>

                    {/* Confirm Replace All warning */}
                    {confirmingReplaceAll && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl">
                            <span className="text-[11px] text-amber-700 dark:text-amber-400 flex-1">
                                {vn
                                    ? `Thay ${matches.length} kết quả trong ${grouped.size} ghi chú?`
                                    : `Replace ${matches.length} match${matches.length !== 1 ? 'es' : ''} across ${grouped.size} note${grouped.size !== 1 ? 's' : ''}?`}
                            </span>
                            <button onClick={() => setConfirmingReplaceAll(false)}
                                className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 hover:text-red-600 transition-colors shrink-0 underline">
                                {vn ? 'Huỷ' : 'Cancel'}
                            </button>
                            <button onClick={handleConfirmReplaceAll}
                                className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors shrink-0">
                                {vn ? 'Xác nhận' : 'Confirm'}
                            </button>
                        </div>
                    )}

                    {/* AI Judge — hint input + button */}
                    <div className="space-y-1.5">
                        <input
                            type="text"
                            value={judgeHint}
                            onChange={e => setJudgeHint(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !aiRunning && query.trim() && matches.length > 0) handleAIAssist(); }}
                            placeholder={vn
                                ? 'Gợi ý cho AI: mô tả khi nào nên thay (vd: chỉ thay khi T là ký hiệu transpose)…'
                                : 'AI hint: describe when to replace (e.g. only when T means matrix transpose)…'}
                            className="w-full px-3 py-2 text-[12px] bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-700/50 rounded-xl outline-none focus:border-violet-400 dark:focus:border-violet-500 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
                        />
                        <button
                            onClick={aiRunning ? () => { aiAbortRef.current = true; } : handleAIAssist}
                            disabled={!aiRunning && (!query.trim() || matches.length === 0)}
                            className={`w-full flex items-center justify-center gap-2 py-2 text-[12px] font-semibold rounded-xl transition-colors disabled:opacity-40 ${
                                aiRunning
                                    ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700'
                                    : hasAnyAIResult
                                        ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-600'
                                        : 'bg-violet-600 hover:bg-violet-700 text-white'
                            }`}
                        >
                            <Sparkles size={13} />
                            {aiRunning
                                ? (vn
                                    ? `Dừng AI (${aiProgress ? `${aiProgress.done}/${aiProgress.total}` : '…'})`
                                    : `Stop AI (${aiProgress ? `${aiProgress.done}/${aiProgress.total}` : '…'})`)
                                : hasAnyAIResult
                                    ? (vn ? 'Chạy lại AI' : 'Re-run AI judge')
                                    : (vn ? 'Để AI quyết định' : 'Let AI Decide')}
                        </button>
                    </div>

                    {/* Status bar */}
                    <div className="flex items-center justify-between min-h-[16px]">
                        {query.trim() ? (
                            matches.length > 0 ? (
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                                    {isOrphanMode && (
                                        <span className="px-1.5 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-semibold">
                                            {vn ? '** côi' : 'orphan **'}
                                        </span>
                                    )}
                                    {matches.length} {vn ? 'kết quả trong' : 'matches in'} {grouped.size} {vn ? 'ghi chú' : 'notes'}
                                </p>
                            ) : (
                                <p className="text-[11px] text-gray-400 italic">{vn ? 'Không tìm thấy' : 'No matches'}</p>
                            )
                        ) : (
                            <p className="text-[11px] text-gray-400">{vn ? 'Tìm trong toàn bộ notebook' : 'Search across all notes'}</p>
                        )}
                        {replacedCount !== null && (
                            <p className="text-[11px] text-green-600 dark:text-green-400 font-semibold">
                                ✓ {replacedCount} {vn ? 'đã thay' : 'replaced'}
                            </p>
                        )}
                    </div>
                </div>

                {/* AI Assistant — collapsible */}
                <div className="border-b dark:border-slate-700 shrink-0">
                    <button
                        onClick={() => setChatOpen(v => !v)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-[11px] font-semibold text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/10 transition-colors"
                    >
                        <Sparkles size={11} />
                        <span className="flex-1 text-left">AI Assistant</span>
                        {aiRunning && (
                            <span className="text-[10px] text-violet-500 font-mono">
                                {aiProgress ? `${aiProgress.done}/${aiProgress.total}…` : '…'}
                            </span>
                        )}
                        {chatOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>

                    {chatOpen && (
                        <div className="px-4 pb-3 space-y-2">

                            {/* Chat messages */}
                            {(chatMessages.length > 0 || chatLoading) && (
                                <div className="max-h-44 overflow-y-auto space-y-1.5 pr-1 pt-1">
                                    {chatMessages.map((msg, i) => (
                                        <div key={i} className={`text-[11px] leading-relaxed px-2.5 py-1.5 rounded-xl ${
                                            msg.role === 'user'
                                                ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-900 dark:text-violet-200 ml-6'
                                                : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 mr-6'
                                        }`}>
                                            {msg.text}
                                        </div>
                                    ))}
                                    {chatLoading && (
                                        <div className="bg-gray-100 dark:bg-slate-700 text-gray-400 text-[11px] px-2.5 py-1.5 rounded-xl mr-6">
                                            <span className="animate-pulse">…</span>
                                        </div>
                                    )}
                                    <div ref={chatEndRef} />
                                </div>
                            )}

                            {/* AI results — threshold + apply */}
                            {hasAnyAIResult && !aiRunning && (
                                <div className="space-y-1.5 pt-0.5">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] text-gray-400 shrink-0">{vn ? 'Ngưỡng' : 'Min confidence'}</span>
                                        <input type="range" min={50} max={100} step={5} value={aiThreshold}
                                            onChange={e => setAiThreshold(Number(e.target.value))}
                                            className="flex-1 accent-indigo-500 h-1 cursor-pointer" />
                                        <span className="text-[11px] font-mono text-indigo-600 dark:text-indigo-400 w-8 text-right shrink-0">{aiThreshold}%</span>
                                    </div>
                                    <div className="flex gap-1.5">
                                        <button onClick={handleApplyAIChoices}
                                            className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] font-semibold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white transition-colors">
                                            <Check size={11} />
                                            {vn ? `Áp dụng ≥${aiThreshold}% (${aiReplaceCount})` : `Apply ≥${aiThreshold}% (${aiReplaceCount})`}
                                        </button>
                                        <button onClick={() => setAiResults({})}
                                            title={vn ? 'Xoá kết quả AI' : 'Clear AI results'}
                                            className="p-1.5 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-400 hover:text-red-500 hover:border-red-300 transition-colors">
                                            <X size={13} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Chat input row */}
                            <div className="flex gap-1.5">
                                <input
                                    type="text"
                                    value={chatInput}
                                    onChange={e => setChatInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
                                    placeholder={vn ? 'Nhắn gì đó…' : 'Ask something…'}
                                    disabled={chatLoading}
                                    className="flex-1 px-3 py-1.5 text-[11px] bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl outline-none focus:border-violet-400 text-gray-700 dark:text-gray-300 placeholder-gray-400 disabled:opacity-50"
                                />
                                <button onClick={handleChatSend} disabled={!chatInput.trim() || chatLoading}
                                    title={vn ? 'Gửi' : 'Send'}
                                    className="p-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-40 transition-colors shrink-0">
                                    <Send size={13} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Undo stack */}
                {undoStack.length > 0 && (
                    <div className="px-5 py-2 bg-green-50 dark:bg-green-900/20 border-b dark:border-slate-700 shrink-0 space-y-1 max-h-36 overflow-y-auto">
                        {undoStack.map((entry, i) => {
                            const node = nodes.find(n => n.id === entry.nodeId);
                            return (
                                <div key={i} className="flex items-start gap-2">
                                    <div className="flex-1 min-w-0 text-[11px] text-green-700 dark:text-green-400 leading-snug">
                                        ✓ {vn ? 'Đã thay trong' : 'Replaced in'}{' '}
                                        <button
                                            onClick={() => handleNoteClick(entry.nodeId)}
                                            className="font-semibold underline text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer">
                                            {node?.title || '(Untitled)'}
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => handleUndo(entry)}
                                        className="flex items-center gap-1 text-[11px] font-semibold text-green-700 dark:text-green-400 hover:text-red-600 dark:hover:text-red-400 transition-colors shrink-0 pt-0.5">
                                        <RotateCcw size={11} />
                                        {vn ? 'Hoàn tác' : 'Undo'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Match list */}
                {matches.length > 0 && (
                    <div className="flex-1 overflow-y-auto">
                        {[...grouped.entries()].map(([nodeId, nodeMatches]) => (
                            <div key={nodeId} className="border-b dark:border-slate-700/50 last:border-b-0">
                                <button
                                    onClick={() => handleNoteClick(nodeId)}
                                    className="w-full flex items-center gap-2 px-5 py-2 bg-gray-50/80 dark:bg-slate-900/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left group">
                                    <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 truncate flex-1 group-hover:underline">
                                        {nodeMatches[0].nodeTitle || (vn ? '(Không có tiêu đề)' : '(Untitled)')}
                                    </span>
                                    <span className="text-[10px] text-gray-400 shrink-0 bg-gray-200 dark:bg-slate-700 px-1.5 py-0.5 rounded-full">
                                        {nodeMatches.length}
                                    </span>
                                </button>

                                {nodeMatches.map((match, i) => {
                                    const key = matchKey(match);
                                    const aiR = aiResults[key];
                                    const aiD = aiR?.decision;
                                    const aiPct = aiR?.confidence ?? 0;
                                    const belowThreshold = aiD === 'replace' && aiPct < aiThreshold;
                                    const isSkipped = aiD === 'skip';
                                    return (
                                        <div key={i} onClick={() => handleNoteClick(nodeId)}
                                            className={`flex items-center gap-3 pl-6 pr-4 py-2 border-t border-gray-50 dark:border-slate-700/30 cursor-pointer transition-colors ${isSkipped ? 'opacity-35' : 'hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}>
                                            {aiD && (
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        const next: AIDecision = aiD === 'replace' ? 'skip' : aiD === 'skip' ? 'uncertain' : 'replace';
                                                        setAiResults(prev => ({ ...prev, [key]: { ...prev[key], decision: next } }));
                                                    }}
                                                    title={aiR?.reason || (vn ? 'Click để đổi quyết định' : 'Click to change decision')}
                                                    className={`shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-lg text-[10px] font-bold border transition-colors ${
                                                        aiD === 'replace'
                                                            ? belowThreshold
                                                                ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700'
                                                                : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300 dark:border-green-700'
                                                            : aiD === 'skip'
                                                                ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 border-gray-200 dark:border-slate-600'
                                                                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700'
                                                    }`}>
                                                    {aiD === 'replace' ? <Check size={9} /> : aiD === 'skip' ? <Minus size={9} /> : <HelpCircle size={9} />}
                                                    <span className="ml-0.5">{aiPct}%</span>
                                                </button>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <span className="text-[9px] font-bold uppercase tracking-wider text-gray-300 dark:text-gray-600 mr-1.5">
                                                    {match.field === 'title' ? (vn ? 'tiêu đề' : 'title') : (vn ? 'nội dung' : 'body')}
                                                </span>
                                                <span className="text-[12px] text-gray-600 dark:text-gray-400 font-mono leading-snug">
                                                    {match.context.slice(0, match.contextMatchStart)}
                                                    <mark className="bg-yellow-200 dark:bg-yellow-500/40 text-gray-900 dark:text-yellow-200 rounded-sm px-0.5 font-semibold not-italic">
                                                        {match.context.slice(match.contextMatchStart, match.contextMatchEnd)}
                                                    </mark>
                                                    {match.context.slice(match.contextMatchEnd)}
                                                </span>
                                            </div>
                                            <button
                                                onClick={e => { e.stopPropagation(); handleReplaceOne(match); }}
                                                className="shrink-0 px-2.5 py-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-slate-500 rounded-lg hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-colors">
                                                {vn ? 'Thay' : 'Replace'}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}

                <MathSymbolPickerModal
                    isOpen={symbolPickerOpen}
                    onClose={() => { setSymbolPickerOpen(false); replaceRef.current?.focus(); }}
                    onInsert={(symbol) => {
                        const input = replaceRef.current;
                        if (input) {
                            const start = input.selectionStart ?? replacement.length;
                            const end = input.selectionEnd ?? replacement.length;
                            const newVal = replacement.slice(0, start) + symbol.unicode + replacement.slice(end);
                            setReplacement(newVal);
                            setTimeout(() => {
                                input.focus();
                                input.selectionStart = input.selectionEnd = start + symbol.unicode.length;
                            }, 0);
                        } else {
                            setReplacement(prev => prev + symbol.unicode);
                        }
                        setSymbolPickerOpen(false);
                    }}
                />
            </div>
        </>
    );
};
